import styled, { css } from 'styled-components'
import AiSparkle from '../../../../assets/icons/ai-sparkle.svg'
import CloseIcon from '../../../../assets/icons/close.svg'

/**
 * BottomSheet renders its header/content/footer slots as row flex containers, so a wrapper without
 * an explicit width is sized shrink-to-fit and collapses its children to their intrinsic width.
 */
const slot = css`
    width: 100%;
    box-sizing: border-box;
`

export const HeaderRow = styled.div(
    ({ theme }) => css`
        ${slot};
        display: flex;
        align-items: center;
        gap: ${theme.spacing.xSmall};
        padding: ${theme.spacing.small} ${theme.spacing.medium} 0;
    `,
)

export const SparkleIcon = styled(AiSparkle)(
    ({ theme }) => css`
        flex-shrink: 0;
        width: ${theme.typography.font.size.large};
        height: ${theme.typography.font.size.large};

        /* A global \`*\` rule sets color on every element, including <path>, which beats inheriting
           from this svg — so currentColor resolves to the body text colour. Set fill directly. */
        path {
            fill: ${theme.colors.tfwViolet};
        }
    `,
)

export const HeaderTitle = styled.h2(
    ({ theme }) => css`
        flex: 1;
        min-width: 0;
        margin: 0;
        color: ${theme.colors.tfwBlack87};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.medium};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.625rem;
    `,
)

export const CloseButton = styled.button(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: ${theme.colors.tfwBlack45};
        cursor: pointer;
    `,
)

export const CloseButtonIcon = styled(CloseIcon)(
    ({ theme }) => css`
        width: ${theme.typography.font.size.small};
        height: ${theme.typography.font.size.small};
    `,
)

export const ContentWrapper = styled.div(
    ({ theme }) => css`
        ${slot};
        display: flex;
        flex-direction: column;
        gap: 0.875rem;
        padding: 0.875rem ${theme.spacing.medium} 0;
    `,
)

export const Divider = styled.div(
    ({ theme }) => css`
        width: 100%;
        height: ${theme.typography.border.width.xxxThin};
        background-color: ${theme.colors.tfwGrey};
    `,
)

export const SuggestionsLabel = styled.p(
    ({ theme }) => css`
        margin: 0;
        color: ${theme.colors.tfwBlack45};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.xSmall};
        font-weight: ${theme.typography.font.lato.weight.regular};
        letter-spacing: 0.0125rem;
        line-height: 1.125rem;
    `,
)

export const SuggestionList = styled.div(
    ({ theme }) => css`
        display: flex;
        flex-wrap: wrap;
        gap: ${theme.spacing.xSmall};
        width: 100%;
    `,
)

export const SuggestionChip = styled.button(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        max-width: 100%;
        padding: 0.3125rem ${theme.spacing.small};
        border: ${theme.typography.border.width.xxxThin} solid ${theme.colors.tfwDefaultInputBorder};
        border-radius: 8rem;
        background-color: ${theme.colors.white};
        color: ${theme.colors.tfwBlack87};
        font-family: ${theme.typography.font.family.lato};
        font-size: 0.8125rem;
        line-height: 1.25rem;
        text-align: left;
        cursor: pointer;

        /* Keeps a long translation from forcing a horizontal scroll at 320px. */
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `,
)

export const FooterWrapper = styled.div(
    ({ theme }) => css`
        ${slot};
        padding: 0.875rem ${theme.spacing.medium} ${theme.spacing.medium};
    `,
)

export const SubmitButton = styled.button(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        justify-content: center;
        gap: ${theme.spacing.xSmall};
        width: 100%;
        box-sizing: border-box;
        padding: 0.8125rem 0;
        border: 0;
        border-radius: ${theme.typography.border.radius.xLarge};
        background-color: ${theme.colors.tfwYellow};
        color: ${theme.colors.tfwBlack87};
        font-family: ${theme.typography.font.family.lato};
        font-size: 0.9375rem;
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        letter-spacing: -0.00625rem;
        cursor: pointer;

        &:disabled {
            background-color: ${theme.colors.tfwBlack12};
            color: ${theme.colors.tfwBlack38};
            cursor: not-allowed;
        }
    `,
)
